import {
  IconButton,
  Menu,
  Tooltip,
  Typography,
  Box,
  ListSubheader,
} from "@mui/material";
import { useCommands, useActive, useEditorState } from "@remirror/react";
import type { EditorState } from "@remirror/pm/state";
import type { Mark } from "@remirror/pm/model";
import { CommandMenuItem } from "@remirror/react-ui";
import { useAtom } from "jotai";
import { useMemo, useState } from "react";
import { sessionAtom } from "../../../../features/accounts/account.state";
import { uiConfigAtom, getFonts } from "../../../../features/design/ui.config";
import { devLog } from "../../../../services/util";

// Helper function to get current font family for display
const getCurrentFontFamily = (
  active: ReturnType<typeof useActive>,
  state?: EditorState
): string | null => {
  // Debug logging to understand what's available
  devLog("FontFamily Debug - active object:", active);
  devLog("FontFamily Debug - active.fontFamily:", active.fontFamily);
  devLog("FontFamily Debug - state:", state);

  // Based on the example, active.fontFamily() returns true if ANY font family is active
  // But we need to get the actual font family value from the editor state
  if (active.fontFamily && typeof active.fontFamily === "function") {
    const isFontFamilyActive = active.fontFamily();
    devLog("FontFamily Debug - isFontFamilyActive:", isFontFamilyActive);

    if (isFontFamilyActive && state) {
      // Try to get the current font family from the selection
      try {
        const { selection } = state;
        devLog("FontFamily Debug - selection:", selection);

        // Get the marks at the current selection
        const marks = selection.$from.marks();
        devLog("FontFamily Debug - marks:", marks);

        // Find the fontFamily mark
        const fontFamilyMark = marks.find(
          (mark: Mark) => mark.type.name === "fontFamily"
        );
        devLog("FontFamily Debug - fontFamilyMark:", fontFamilyMark);

        if (
          fontFamilyMark &&
          fontFamilyMark.attrs &&
          fontFamilyMark.attrs.fontFamily
        ) {
          devLog(
            "FontFamily Debug - Found font family from mark:",
            fontFamilyMark.attrs.fontFamily
          );
          return fontFamilyMark.attrs.fontFamily;
        }
      } catch (e) {
        devLog("FontFamily Debug - Error getting from selection:", e);
      }

      // Alternative: Try to get from the document at cursor position
      try {
        const { selection } = state;
        const resolvedPos = state.doc.resolve(selection.from);
        devLog("FontFamily Debug - resolvedPos:", resolvedPos);

        // Check marks at this position
        const marksAtPos = resolvedPos.marks();
        devLog("FontFamily Debug - marksAtPos:", marksAtPos);

        const fontFamilyMark = marksAtPos.find(
          (mark: Mark) => mark.type.name === "fontFamily"
        );
        if (
          fontFamilyMark &&
          fontFamilyMark.attrs &&
          fontFamilyMark.attrs.fontFamily
        ) {
          devLog(
            "FontFamily Debug - Found font family from position:",
            fontFamilyMark.attrs.fontFamily
          );
          return fontFamilyMark.attrs.fontFamily;
        }
      } catch (e) {
        devLog("FontFamily Debug - Error getting from position:", e);
      }
    }
  }

  devLog("FontFamily Debug - No font family found");
  return null;
};

// Helper function to truncate font name for display
const truncateFontName = (fontName: string, maxLength: number = 12): string => {
  if (fontName.length <= maxLength) {
    return fontName;
  }

  // Try to show first word if it's meaningful
  const firstWord = fontName.split(" ")[0];
  if (firstWord.length <= maxLength && firstWord.length >= 4) {
    return firstWord + "...";
  }

  // Otherwise truncate with ellipsis
  return fontName.substring(0, maxLength - 3) + "...";
};

// Helper function to get font display name from font list
const getFontDisplayName = (
  fontName: string,
  fonts: Array<{ Name: string; Description: string; IsGroup?: boolean }>
): string => {
  const font = fonts.find((f) => f.Name === fontName && !f.IsGroup);
  return font ? font.Description : fontName;
};

export const FontFamilyButton = () => {
  const { setFontFamily } = useCommands();
  const active = useActive();
  const state = useEditorState();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [session] = useAtom(sessionAtom);
  const [uiConfig] = useAtom(uiConfigAtom);

  const fonts = useMemo(
    () => getFonts(session.account, uiConfig),
    [session.account, uiConfig]
  );

  // Get current font family for display on button
  const currentFontFamily = getCurrentFontFamily(active, state);
  const currentFontDisplayName = currentFontFamily
    ? getFontDisplayName(currentFontFamily, fonts)
    : null;
  const displayName = currentFontDisplayName
    ? truncateFontName(currentFontDisplayName)
    : null;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFontSelect = (fontName: string) => {
    setFontFamily(fontName);
    handleClose();
  };

  return (
    <>
      <Tooltip
        title={
          currentFontDisplayName
            ? `Font family: ${currentFontDisplayName}`
            : "Font family"
        }
      >
        <IconButton
          aria-label="Set font family"
          aria-controls={open ? "font-family-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          size="small"
          sx={(theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: `${theme.shape.borderRadius}px`,
            padding: "6px 8px",
            minWidth: "48px",
            maxWidth: "140px",
            height: "30px", // Match exact height of other buttons
            "&:not(:first-of-type)": {
              marginLeft: "-1px",
              borderLeft: "1px solid transparent",
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            },
            "&:not(:last-of-type)": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              width: "100%",
              overflow: "hidden",
            }}
          >
            {displayName ? (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  fontFamily: currentFontFamily || "inherit",
                  mt: "2px",
                }}
              >
                {displayName}
              </Typography>
            ) : (
              <Box sx={{ width: "1rem", height: "1rem" }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M13 6v15h-2V6H5V4h14v2z" />
                </svg>
              </Box>
            )}
            <Box sx={{ width: "1rem", height: "1rem", flexShrink: 0 }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </Box>
          </Box>
        </IconButton>
      </Tooltip>
      <Menu
        id="font-family-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "font-family-button",
        }}
      >
        {fonts.map((f) => {
          return f.IsGroup ? (
            <ListSubheader key={f.Name}>{f.Description}</ListSubheader>
          ) : (
            <CommandMenuItem
              key={f.Name}
              commandName="setFontFamily"
              onSelect={() => handleFontSelect(f.Name)}
              enabled={setFontFamily.enabled(f.Name)}
              active={active.fontFamily({ fontFamily: f.Name })}
              label={
                <span style={{ fontFamily: f.Name }}>{f.Description}</span>
              }
              icon={null}
              displayDescription={false}
            />
          );
        })}
      </Menu>
    </>
  );
};
